/**
 * Banco de preguntas del Genie — Módulos 1–4 (idea01.md).
 *
 * DATOS, NO LÓGICA: este archivo solo declara las preguntas orientadoras y el
 * peso que cada respuesta aporta a una sospecha topográfica. El scoring vive en
 * `src/services/genieEngine.ts` y la UI en `FloatingGeniePanel.tsx`.
 *
 * Base clínica: idea01.md (Preston & Shapiro / AANEM), módulos 1 y 2.
 */

/** Hipótesis topográfica primaria (¿dónde está la lesión?). */
export type SuspicionCategory = 'neuropatico' | 'miopatico' | 'unm' | 'central';

/** Pesos parciales que una respuesta aporta a cada categoría de sospecha. */
export type SuspicionWeights = Partial<Record<SuspicionCategory, number>>;

/** Una opción de respuesta dentro de una pregunta del Genie. */
export interface GenieOption {
  id: string;
  label: string;
  /** Peso que añade esta opción al vector de sospecha. */
  weights: SuspicionWeights;
  /** Nota clínica corta que justifica la orientación (se muestra al elegirla). */
  rationale?: string;
}

/** Ventana del workflow donde se muestra la pregunta. */
export type GenieWindow = 'symptoms' | 'ncs' | 'emg';

/** Curso temporal clínico para filtrar el catálogo de diagnósticos. */
export type TemporalCourse = 'acute' | 'subacute' | 'chronic';

/** Pregunta orientadora del Genie. */
export interface GenieQuestion {
  id: string;
  /** Módulo clínico de idea01 (1 anamnesis, 2 exploración, 3 NCS, 4 EMG). */
  module: 1 | 2 | 3 | 4;
  window: GenieWindow;
  text: string;
  /** Texto corto de ayuda/encuadre para el clínico. */
  helper?: string;
  options: GenieOption[];
  /** Visible solo si TODAS las condiciones se cumplen. */
  showWhen?: Record<string, string[]>;
  /** Oculta si alguna condición se cumple. */
  hideWhen?: Record<string, string[]>;
}

/** Etiquetas legibles de cada categoría para la UI y el reporte. */
export const SUSPICION_LABELS: Record<SuspicionCategory, string> = {
  neuropatico: 'Neuropático (nervio / raíz / asta anterior)',
  miopatico: 'Miopático (músculo)',
  unm: 'Unión neuromuscular',
  central: 'Central / motoneurona superior',
};

/**
 * Preguntas para la ventana de síntomas (Módulos 1 y 2 de idea01).
 * El orden refleja la secuencia clínica: temporalidad → distribución → fibras
 * → reflejos → signos especiales.
 */
export const GENIE_SYMPTOM_QUESTIONS: GenieQuestion[] = [
  {
    id: 'curso_temporal',
    module: 1,
    window: 'symptoms',
    text: '¿Cuál es el curso temporal de los síntomas?',
    helper: 'La variable clave del filtro inicial (idea01, Módulo 1.1).',
    options: [
      {
        id: 'agudo',
        label: 'Agudo (< 4 semanas)',
        weights: { neuropatico: 2 },
        rationale: 'Orienta a procesos agudos (AIDP/Guillain-Barré, radiculopatía hiperaguda).',
      },
      {
        id: 'subagudo',
        label: 'Subagudo (4–8 semanas)',
        weights: { miopatico: 2, neuropatico: 1 },
        rationale: 'Subagudo: polimiositis, radiculopatía en evolución, GBS tardío.',
      },
      {
        id: 'cronico',
        label: 'Crónico (> 8 semanas)',
        weights: {},
        rationale:
          'Curso crónico: procesos lentos en neuropatía, miopatía o motoneurona; acotar con distribución y reflejos.',
      },
    ],
  },
  {
    id: 'distribucion',
    module: 1,
    window: 'symptoms',
    text: '¿Cómo se distribuye el déficit?',
    helper: 'Distribución y simetría (idea01, Módulo 1.2).',
    options: [
      {
        id: 'simetrico_distal',
        label: 'Simétrico distal ("guante y calcetín")',
        weights: { neuropatico: 4 },
        rationale: 'Patrón clásico de polineuropatía.',
      },
      {
        id: 'simetrico_proximal',
        label: 'Simétrico proximal (cinturas)',
        weights: { miopatico: 4, unm: 3 },
        rationale: 'Debilidad proximal orienta a miopatía o unión neuromuscular.',
      },
      {
        id: 'asimetrico_focal',
        label: 'Asimétrico / focal (un tronco nervioso)',
        weights: { neuropatico: 3 },
        rationale: 'Sugiere mononeuropatía, plexopatía o radiculopatía.',
      },
      {
        id: 'multifocal',
        label: 'Multifocal / parcheado',
        weights: { neuropatico: 3 },
        rationale: 'Compatible con mononeuritis múltiple o neuropatía motora multifocal.',
      },
    ],
  },
  {
    id: 'tipo_fibras',
    module: 1,
    window: 'symptoms',
    text: '¿Qué tipo de síntomas predominan?',
    helper: 'Tipo de fibras afectadas (idea01, Módulo 1.3).',
    options: [
      {
        id: 'motor_puro',
        label: 'Motor puro (debilidad, atrofia)',
        weights: { unm: 3, miopatico: 2, central: 1 },
        rationale: 'Sin compromiso sensitivo: considerar UNM, miopatía o motoneurona.',
      },
      {
        id: 'sensitivo_puro',
        label: 'Sensitivo puro (entumecimiento, dolor)',
        weights: { neuropatico: 3 },
        rationale: 'Compromiso sensitivo orienta a nervio periférico.',
      },
      {
        id: 'mixto',
        label: 'Mixto (motor y sensitivo)',
        weights: { neuropatico: 2 },
        rationale: 'Patrón sensitivomotor típico de polineuropatía o plexopatía.',
      },
    ],
  },
  {
    id: 'reflejos',
    module: 2,
    window: 'symptoms',
    text: '¿Cómo están los reflejos miotáticos?',
    helper: 'Exploración física (idea01, Módulo 2).',
    options: [
      {
        id: 'abolidos_disminuidos',
        label: 'Abolidos / disminuidos',
        weights: { neuropatico: 3, miopatico: 1 },
        rationale: 'Hiporreflexia indica compromiso de motoneurona inferior / nervio.',
      },
      {
        id: 'normales',
        label: 'Normales',
        weights: { miopatico: 1, unm: 1 },
        rationale: 'Reflejos preservados son típicos de miopatía y UNM.',
      },
      {
        id: 'hiperreflexia',
        label: 'Aumentados / Babinski presente',
        weights: { central: 5, neuropatico: 2 },
        rationale: 'Signos de motoneurona superior; en ELA coexisten MNS + MNI.',
      },
    ],
  },
  {
    id: 'fasciculaciones',
    module: 2,
    window: 'symptoms',
    text: '¿Hay fasciculaciones visibles con atrofia?',
    helper: 'Signo especial de motoneurona inferior (idea01, Módulo 2).',
    options: [
      {
        id: 'si',
        label: 'Sí',
        weights: { neuropatico: 3, central: 2 },
        rationale: 'Fasciculaciones con atrofia sugieren enfermedad de motoneurona.',
      },
      { id: 'no', label: 'No', weights: {} },
    ],
  },
  {
    id: 'fatigabilidad',
    module: 2,
    window: 'symptoms',
    text: '¿Existe fatigabilidad fluctuante (empeora con la actividad)?',
    helper: 'Signo especial de unión neuromuscular (idea01, Módulo 2).',
    options: [
      {
        id: 'si',
        label: 'Sí',
        weights: { unm: 6 },
        rationale: 'La fatigabilidad fluctuante es el sello de los trastornos de la UNM.',
      },
      { id: 'no', label: 'No', weights: {} },
    ],
  },
];

/** Preguntas para la ventana NCS (Módulo 3 de idea01). */
export const GENIE_NCS_QUESTIONS: GenieQuestion[] = [
  {
    id: 'ncs_temperatura',
    module: 3,
    window: 'ncs',
    text: '¿La temperatura cutánea se mantuvo ≥32°C en manos y ≥30°C en pies durante el estudio?',
    helper: 'Guardrail de temperatura (idea01, sección 4.1).',
    options: [
      {
        id: 'si',
        label: 'Sí, temperatura adecuada',
        weights: {},
        rationale: 'Resultados de VCN confiables.',
      },
      {
        id: 'no',
        label: 'No / no se midió',
        weights: {},
        rationale:
          'VCN pueden estar falsamente enlentecidas. Calentar extremidad y repetir antes de concluir desmielinización.',
      },
    ],
  },
  {
    id: 'ncs_snap',
    module: 3,
    window: 'ncs',
    text: '¿Cómo están los potenciales sensitivos (SNAP / PASN)?',
    helper: 'Separa lesión pre-ganglionar (radiculopatía/motoneurona) de post-ganglionar.',
    options: [
      {
        id: 'normales',
        label: 'Normales',
        weights: { central: 2 },
        rationale: 'SNAP normal: lesión proximal al ganglio o motoneurona pura.',
      },
      {
        id: 'disminuidos',
        label: 'Disminuidos / ausentes',
        weights: { neuropatico: 3 },
        rationale: 'SNAP anormal: lesión post-ganglionar (nervio, plexo).',
      },
    ],
  },
  {
    id: 'ncs_vcn_patron',
    module: 3,
    window: 'ncs',
    text: '¿Cuál es el patrón predominante en la conducción motora?',
    helper: 'Determina axonal vs desmielinizante (idea01, Módulo 3).',
    options: [
      {
        id: 'axonal',
        label: 'Axonal: amplitudes ↓, VCN normal (>80% LIN)',
        weights: { neuropatico: 3 },
        rationale: 'Patrón de pérdida axonal.',
      },
      {
        id: 'desmielinizante',
        label: 'Desmielinizante: VCN lenta (<70% LIN), latencias ↑',
        weights: { neuropatico: 4 },
        rationale: 'Patrón desmielinizante: CIDP, CMT, GBS.',
      },
      {
        id: 'normal',
        label: 'Normal',
        weights: { miopatico: 1, unm: 1 },
        rationale: 'NCS motora normal: típica de miopatía y UNM.',
      },
    ],
  },
  {
    id: 'ncs_bloqueo',
    module: 3,
    window: 'ncs',
    text: '¿Hay bloqueos de conducción motora (>50% caída proximal) o dispersión temporal?',
    helper: 'Signo de desmielinización adquirida (idea01, Módulo 3).',
    showWhen: { ncs_vcn_patron: ['desmielinizante'] },
    options: [
      {
        id: 'si',
        label: 'Sí, bloqueo / dispersión',
        weights: { neuropatico: 4 },
        rationale: 'Bloqueos fuera de atrapamiento: CIDP, GBS o MMN.',
      },
      { id: 'no', label: 'No', weights: {} },
    ],
  },
];

/** Preguntas para la ventana EMG (Módulo 4 de idea01). */
export const GENIE_EMG_QUESTIONS: GenieQuestion[] = [
  {
    id: 'emg_espontanea',
    module: 4,
    window: 'emg',
    text: '¿Qué actividad espontánea se observa en reposo?',
    helper: 'Actividad en reposo (idea01, Módulo 4.1).',
    options: [
      {
        id: 'silencio',
        label: 'Silencio eléctrico (normal)',
        weights: { unm: 1 },
        rationale: 'Silencio compatible con UNM y patología crónica estable.',
      },
      {
        id: 'fibrilaciones',
        label: 'Fibrilaciones / ondas agudas positivas',
        weights: { neuropatico: 3, miopatico: 2 },
        rationale: 'Denervación activa o miopatía necrótica/inflamatoria.',
      },
      {
        id: 'fasciculaciones',
        label: 'Fasciculaciones patológicas',
        weights: { central: 3, neuropatico: 3 },
        rationale: 'Fasciculaciones con denervación: enfermedad de motoneurona.',
      },
      {
        id: 'descargas_miotonicas',
        label: 'Descargas miotónicas',
        weights: { miopatico: 5 },
        rationale: 'Canalopatía / distrofia miotónica.',
      },
    ],
  },
  {
    id: 'emg_paum_morfologia',
    module: 4,
    window: 'emg',
    text: '¿Cómo es la morfología de los PAUMs (contracción leve)?',
    helper: 'Morfología de unidad motora (idea01, Módulo 4.2).',
    options: [
      {
        id: 'neurogenico',
        label: 'Grandes, larga duración, polifásicos',
        weights: { neuropatico: 4, central: 2 },
        rationale: 'Reinervación colateral: proceso neuropático crónico.',
      },
      {
        id: 'miopatico',
        label: 'Pequeños, cortos, polifásicos',
        weights: { miopatico: 5 },
        rationale: 'Pérdida de fibras musculares: miopatía.',
      },
      {
        id: 'inestables',
        label: 'Inestables (varían potencial a potencial)',
        weights: { unm: 4 },
        rationale: 'Inestabilidad de la unión neuromuscular (MG, LEMS).',
      },
      { id: 'normal', label: 'Normales', weights: {} },
    ],
  },
  {
    id: 'emg_reclutamiento',
    module: 4,
    window: 'emg',
    text: '¿Cómo es el patrón de reclutamiento?',
    helper: 'Esfuerzo máximo (idea01, Módulo 4.3).',
    options: [
      {
        id: 'disminuido',
        label: 'Disminuido (pocos potenciales, alta frecuencia)',
        weights: { neuropatico: 4 },
        rationale: 'Pérdida de unidades motoras: neuropático.',
      },
      {
        id: 'precoz',
        label: 'Precoz (pantalla llena ante esfuerzo mínimo)',
        weights: { miopatico: 4 },
        rationale: 'Reclutamiento precoz: miopático.',
      },
      { id: 'normal', label: 'Normal', weights: { unm: 1 } },
    ],
  },
  {
    id: 'emg_segmentos',
    module: 4,
    window: 'emg',
    text: '¿En cuántas regiones corporales hay alteraciones EMG?',
    helper: 'Criterio de difusión (Gold Coast ELA: ≥2 regiones).',
    options: [
      { id: 'una', label: '1 región (focal)', weights: { neuropatico: 1 } },
      {
        id: 'dos_tres',
        label: '2–3 regiones',
        weights: { neuropatico: 2, central: 3 },
        rationale: 'Denervación en ≥2 regiones: favorece enfermedad de motoneurona.',
      },
      {
        id: 'cuatro_mas',
        label: '≥4 regiones (bulbar, cervical, torácico, lumbar)',
        weights: { central: 5, neuropatico: 2 },
        rationale: 'Diseminación generalizada: altamente compatible con ELA.',
      },
    ],
  },
];

/** Todas las preguntas del Genie (síntomas + NCS + EMG). */
export const GENIE_QUESTIONS: GenieQuestion[] = [
  ...GENIE_SYMPTOM_QUESTIONS,
  ...GENIE_NCS_QUESTIONS,
  ...GENIE_EMG_QUESTIONS,
];

/** Devuelve las preguntas correspondientes a una ventana del workflow. */
export function getGenieQuestionsForWindow(window: GenieWindow): GenieQuestion[] {
  return GENIE_QUESTIONS.filter(q => q.window === window);
}

// ---------------------------------------------------------------------------
// Mapa patrón diagnóstico → categoría(s) de sospecha topográfica
// ---------------------------------------------------------------------------

/**
 * Asocia cada `id` de patrón del motor con la(s) categoría(s) de sospecha que
 * representa. Permite resaltar y reforzar patrones compatibles con la
 * orientación del Genie. Datos, no lógica: añade aquí los patrones nuevos.
 */
export const PATTERN_SUSPICION_CATEGORIES: Record<string, SuspicionCategory[]> = {
  // Core
  carpal_tunnel_syndrome: ['neuropatico'],
  polyneuropathy: ['neuropatico'],
  radiculopathy: ['neuropatico'],
  mononeuropathy: ['neuropatico'],
  plexopathy: ['neuropatico'],
  myopathy: ['miopatico'],
  myasthenia_gravis: ['unm'],
  motor_neuron_disease: ['neuropatico', 'central'],
  // Expandidos
  cidp: ['neuropatico'],
  gbs: ['neuropatico'],
  multifocal_motor_neuropathy: ['neuropatico'],
  ulnar_neuropathy_elbow: ['neuropatico'],
  peroneal_neuropathy: ['neuropatico'],
  small_fiber_neuropathy: ['neuropatico'],
  axonal_cold_temperature_corrected: ['neuropatico'],
  inflammatory_myopathy: ['miopatico'],
  myotonic_dystrophy: ['miopatico'],
  lambert_eaton: ['unm'],
  // `ncs_modern_criteria_alerts` es una alerta técnica, sin categoría topográfica.
};

/** Compacto de la orientación del Genie para adjuntar a `ClinicalReport`. */
export interface GenieOrientation {
  /** Respuestas crudas: questionId -> optionId. */
  answers: Record<string, string>;
  topCategory: SuspicionCategory | null;
  topLabel: string | null;
  /** Puntaje normalizado 0–1 de la sospecha dominante. */
  normalized: number;
  isAmbiguous: boolean;
}

// ---------------------------------------------------------------------------
// Preguntas de desempate (diferenciales críticos) — Módulos 3 y 4
// ---------------------------------------------------------------------------

export interface GenieTiebreakerOption {
  id: string;
  label: string;
  /** Conducta clínica sugerida si se elige esta opción. */
  resolution: string;
  /** Ajustes de confianza: patrón id → delta (+ refuerza, − penaliza). */
  patternAdjustments?: Record<string, number>;
}

/** Pregunta de desempate que solo aparece ante un conflicto entre patrones. */
export interface GenieTiebreaker {
  id: string;
  /** Se activa si TODOS estos patrones están presentes en el análisis. */
  requiresPatterns: string[];
  title: string;
  question: string;
  options: GenieTiebreakerOption[];
}

/**
 * Desempates de diferenciales críticos (idea01, árboles 3 y 4).
 * Solo se muestran cuando el análisis detecta los dos patrones en conflicto.
 */
export const GENIE_TIEBREAKERS: GenieTiebreaker[] = [
  {
    id: 'ela_vs_mmn',
    requiresPatterns: ['motor_neuron_disease', 'multifocal_motor_neuropathy'],
    title: 'Desempate: ELA vs Neuropatía Motora Multifocal (MMN)',
    question:
      '¿Hay bloqueo de conducción motora en un segmento atípico (fuera de sitios de atrapamiento) con SNAP normal?',
    options: [
      {
        id: 'bloqueo_atipico',
        label: 'Sí, bloqueo en segmento atípico',
        resolution:
          'Favorece NMM (tratable): priorizar anticuerpos anti-GM1, ecografía de nervio e inmunoglobulina.',
        patternAdjustments: {
          multifocal_motor_neuropathy: 0.1,
          motor_neuron_disease: -0.1,
        },
      },
      {
        id: 'sin_bloqueo',
        label: 'No, sin bloqueos de conducción',
        resolution:
          'Favorece ELA: aplicar criterios Gold Coast / Awaji y seguimiento neuromuscular estrecho.',
        patternAdjustments: {
          motor_neuron_disease: 0.08,
          multifocal_motor_neuropathy: -0.12,
        },
      },
    ],
  },
  {
    id: 'mg_vs_lems',
    requiresPatterns: ['myasthenia_gravis', 'lambert_eaton'],
    title: 'Desempate: Miastenia Gravis (MG) vs Lambert-Eaton (LEMS)',
    question:
      '¿Qué ocurre con la amplitud del CMAP tras 10 s de ejercicio voluntario máximo?',
    options: [
      {
        id: 'facilitacion',
        label: 'Incremento / facilitación > 100%',
        resolution:
          'Favorece LEMS (presináptico): solicitar anticuerpos VGCC y descartar neoplasia subyacente.',
        patternAdjustments: {
          lambert_eaton: 0.12,
          myasthenia_gravis: -0.1,
        },
      },
      {
        id: 'decremento',
        label: 'Decremento a 3 Hz, sin facilitación',
        resolution:
          'Favorece MG (postsináptico): anticuerpos AChR/MuSK y jitter en EMG de fibra única.',
        patternAdjustments: {
          myasthenia_gravis: 0.1,
          lambert_eaton: -0.12,
        },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Catálogo de diagnósticos de sospecha inicial
// ---------------------------------------------------------------------------

/** Id especial para "el médico no tiene sospecha inicial". */
export const UNKNOWN_SUSPICION_ID = 'unknown';

/** Entrada del catálogo de diagnósticos que el médico puede sospechar al inicio. */
export interface GenieDiagnosisCatalogEntry {
  /** Coincide con el `id` del patrón del motor (o `UNKNOWN_SUSPICION_ID`). */
  id: string;
  name: string;
  /** Categoría topográfica para agrupar en la UI. */
  category: SuspicionCategory;
  /** Cursos temporales compatibles. Omisión = todos. */
  temporalFit?: TemporalCourse[];
}

/** Regla de razonamiento compuesto (idea01, matriz clínica). */
export interface CompositeRationaleRule {
  /** Condiciones: questionId → optionIds válidos (AND entre claves). */
  when: Record<string, string[]>;
  message: string;
  /** Ids del catálogo a resaltar como compatibles. */
  highlightCatalogIds?: string[];
}

/** Regla declarativa de advertencia de coherencia clínica. */
export interface GenieWarningRule {
  when: Record<string, string[]>;
  message: string;
  /** Si true, requiere categoría topográfica dominante miopático. */
  requiresTopMiopatico?: boolean;
  /** Si true, requiere patrón GBS en diagnósticos en vivo. */
  requiresLiveGbs?: boolean;
}

/** Mapeo curso_temporal → TemporalCourse. */
export const CURSO_TO_TEMPORAL: Record<string, TemporalCourse> = {
  agudo: 'acute',
  subagudo: 'subacute',
  cronico: 'chronic',
};

/** Todos los cursos temporales (default del catálogo). */
export const ALL_TEMPORAL_COURSES: TemporalCourse[] = ['acute', 'subacute', 'chronic'];

/**
 * Catálogo de diagnósticos seleccionables como sospecha inicial. Los `id`
 * coinciden con los patrones del motor (`DiagnosticPatternAnalyzer` /
 * `ExpandedPatternAnalyzer`) para poder contrastar la sospecha del médico con
 * los hallazgos en vivo. DATOS, NO LÓGICA.
 */
export const GENIE_DIAGNOSIS_CATALOG: GenieDiagnosisCatalogEntry[] = [
  // Neuropático (nervio / raíz / asta anterior)
  {
    id: 'carpal_tunnel_syndrome',
    name: 'Síndrome del Túnel Carpiano',
    category: 'neuropatico',
    temporalFit: ['chronic'],
  },
  {
    id: 'ulnar_neuropathy_elbow',
    name: 'Neuropatía Cubital en Codo',
    category: 'neuropatico',
    temporalFit: ['chronic'],
  },
  {
    id: 'peroneal_neuropathy',
    name: 'Neuropatía Peronea',
    category: 'neuropatico',
    temporalFit: ['chronic'],
  },
  {
    id: 'mononeuropathy',
    name: 'Mononeuropatía (otra)',
    category: 'neuropatico',
    temporalFit: ['acute', 'subacute', 'chronic'],
  },
  {
    id: 'polyneuropathy',
    name: 'Polineuropatía',
    category: 'neuropatico',
    temporalFit: ['chronic'],
  },
  {
    id: 'small_fiber_neuropathy',
    name: 'Neuropatía de Fibras Delgadas',
    category: 'neuropatico',
    temporalFit: ['chronic'],
  },
  {
    id: 'radiculopathy',
    name: 'Radiculopatía',
    category: 'neuropatico',
    temporalFit: ['acute', 'subacute', 'chronic'],
  },
  {
    id: 'plexopathy',
    name: 'Plexopatía',
    category: 'neuropatico',
    temporalFit: ['acute', 'subacute', 'chronic'],
  },
  { id: 'cidp', name: 'CIDP', category: 'neuropatico', temporalFit: ['chronic'] },
  { id: 'gbs', name: 'Síndrome de Guillain-Barré', category: 'neuropatico', temporalFit: ['acute'] },
  {
    id: 'multifocal_motor_neuropathy',
    name: 'Neuropatía Motora Multifocal (NMM)',
    category: 'neuropatico',
    temporalFit: ['chronic'],
  },
  {
    id: 'motor_neuron_disease',
    name: 'Enfermedad de Motoneurona / ELA',
    category: 'neuropatico',
    temporalFit: ['chronic'],
  },
  // Miopático (músculo)
  {
    id: 'myopathy',
    name: 'Miopatía',
    category: 'miopatico',
    temporalFit: ['subacute', 'chronic'],
  },
  {
    id: 'inflammatory_myopathy',
    name: 'Miopatía Inflamatoria',
    category: 'miopatico',
    temporalFit: ['subacute', 'chronic'],
  },
  {
    id: 'myotonic_dystrophy',
    name: 'Distrofia Miotónica',
    category: 'miopatico',
    temporalFit: ['chronic'],
  },
  // Unión neuromuscular
  { id: 'myasthenia_gravis', name: 'Miastenia Gravis', category: 'unm' },
  { id: 'lambert_eaton', name: 'Síndrome de Lambert-Eaton (LEMS)', category: 'unm' },
];

/**
 * Reglas de razonamiento compuesto (idea01, Módulos 1–4).
 * Orden: de mayor a menor especificidad deseada; el motor ordena por nº de condiciones.
 */
export const GENIE_COMPOSITE_RULES: CompositeRationaleRule[] = [
  // idea01 Módulo 1.2 + 1.3 — polineuropatía crónica
  {
    when: {
      curso_temporal: ['cronico'],
      distribucion: ['simetrico_distal'],
      tipo_fibras: ['mixto'],
    },
    message:
      'Patrón crónico distal simétrico mixto: polineuropatía (tóxica, metabólica, hereditaria/CMT, CIDP…).',
    highlightCatalogIds: ['polyneuropathy', 'cidp', 'small_fiber_neuropathy'],
  },
  // idea01 Módulo 1.2 — miopatía / UNM proximal
  {
    when: {
      curso_temporal: ['cronico', 'subagudo'],
      distribucion: ['simetrico_proximal'],
      reflejos: ['abolidos_disminuidos', 'normales'],
    },
    message: 'Debilidad proximal: miopatía (inflamatoria, endocrina, tóxica) vs unión neuromuscular.',
    highlightCatalogIds: ['myopathy', 'inflammatory_myopathy', 'myasthenia_gravis', 'lambert_eaton'],
  },
  // idea01 Módulo 2 — motoneurona
  {
    when: {
      curso_temporal: ['cronico'],
      tipo_fibras: ['motor_puro'],
      fasciculaciones: ['si'],
    },
    message: 'Motor puro crónico con fasciculaciones: enfermedad de motoneurona (ELA); diferencial con MMN según NCS.',
    highlightCatalogIds: ['motor_neuron_disease', 'multifocal_motor_neuropathy'],
  },
  // idea01 Módulo 1.1 agudo
  {
    when: { curso_temporal: ['agudo'], distribucion: ['simetrico_distal'] },
    message: 'Agudo distal simétrico: AIDP/Guillain-Barré vs radiculopatía hiperaguda múltiple.',
    highlightCatalogIds: ['gbs', 'radiculopathy'],
  },
  // idea01 Módulo 3 — SNAP normal + motor
  {
    when: { ncs_snap: ['normales'], tipo_fibras: ['motor_puro'] },
    message: 'SNAP normales con déficit motor: radiculopatía o motoneurona pura (ELA, MMN).',
    highlightCatalogIds: ['radiculopathy', 'motor_neuron_disease', 'multifocal_motor_neuropathy'],
  },
  // idea01 Módulo 3 — desmielinizante
  {
    when: { ncs_snap: ['disminuidos'], ncs_vcn_patron: ['desmielinizante'] },
    message: 'Desmielinización con SNAP alterados: CIDP (crónico), GBS (agudo) o neuropatía hereditaria (CMT).',
    highlightCatalogIds: ['cidp', 'gbs', 'polyneuropathy'],
  },
  // idea01 Módulo 2 + 3 — UNM
  {
    when: { fatigabilidad: ['si'], ncs_vcn_patron: ['normal'] },
    message: 'Fatigabilidad con NCS motora normal: miastenia gravis o síndrome de Lambert-Eaton.',
    highlightCatalogIds: ['myasthenia_gravis', 'lambert_eaton'],
  },
  // Focal / mononeuropatía
  {
    when: { distribucion: ['asimetrico_focal'] },
    message: 'Patrón focal: mononeuropatía, radiculopatía o plexopatía según territorio.',
    highlightCatalogIds: [
      'carpal_tunnel_syndrome',
      'ulnar_neuropathy_elbow',
      'peroneal_neuropathy',
      'mononeuropathy',
      'radiculopathy',
      'plexopathy',
    ],
  },
  // Multifocal
  {
    when: { distribucion: ['multifocal'] },
    message: 'Patrón multifocal: neuropatía motora multifocal, mononeuritis múltiple o vasculitis.',
    highlightCatalogIds: ['multifocal_motor_neuropathy', 'mononeuropathy'],
  },
  // Subagudo miopático
  {
    when: { curso_temporal: ['subagudo'], distribucion: ['simetrico_proximal'] },
    message: 'Subagudo proximal: polimiositis/dermatomiositis vs miopatía tóxica/endocrina.',
    highlightCatalogIds: ['inflammatory_myopathy', 'myopathy'],
  },
  // Solo crónico (fallback)
  {
    when: { curso_temporal: ['cronico'] },
    message:
      'Curso crónico: amplía el diferencial a neuropatías, miopatías y enfermedad de motoneurona. Complete distribución y reflejos.',
  },
  // Solo agudo (fallback)
  {
    when: { curso_temporal: ['agudo'] },
    message: 'Curso agudo: priorice GBS/AIDP, radiculopatía aguda o mononeuropatía compresiva aguda.',
    highlightCatalogIds: ['gbs', 'radiculopathy', 'mononeuropathy'],
  },
];

/** Reglas de advertencia de coherencia clínico-electrofisiológica. */
export const GENIE_WARNING_RULES: GenieWarningRule[] = [
  {
    when: { curso_temporal: ['agudo'], ncs_vcn_patron: ['desmielinizante'] },
    message:
      'Curso agudo + patrón desmielinizante: favorece GBS/AIDP; CIDP es poco probable como primera opción.',
  },
  {
    when: { curso_temporal: ['cronico'] },
    requiresLiveGbs: true,
    message: 'Incoherencia temporal: GBS es agudo/subagudo; reconsiderar CIDP u otra neuropatía crónica.',
  },
  {
    when: { fasciculaciones: ['si'], ncs_snap: ['disminuidos'] },
    message:
      'Fasciculaciones con SNAP alterados: SNAP normal es típico en ELA pura; reconsiderar CIDP mixta o plexopatía.',
  },
  {
    when: { ncs_vcn_patron: ['normal'], emg_espontanea: ['fibrilaciones'] },
    requiresTopMiopatico: true,
    message:
      'NCS normal con fibrilaciones en EMG y sospecha miopática: miopatía necrótica/inflamatoria vs radiculopatía aguda.',
  },
  {
    when: { fatigabilidad: ['si'], emg_espontanea: ['fasciculaciones'] },
    message:
      'Fatigabilidad + fasciculaciones en EMG: combinación atípica de UNM pura; correlacionar clínica y RNS/SFEMG.',
  },
];

/** Nombre legible de un id de diagnóstico del catálogo. */
export function getDiagnosisName(id: string): string {
  if (id === UNKNOWN_SUSPICION_ID) return 'Sin sospecha inicial / desconocido';
  return GENIE_DIAGNOSIS_CATALOG.find(d => d.id === id)?.name ?? id;
}

// ---------------------------------------------------------------------------
// Sugerencias por sección según la sospecha topográfica dominante
// ---------------------------------------------------------------------------

/**
 * Sugerencias de estudios/maniobras por ventana del workflow y categoría de
 * sospecha. Orientan al médico sobre qué reforzar o complementar en cada paso.
 * DATOS, NO LÓGICA: la selección vive en `genieEngine.getGenieSectionSuggestions`.
 */
export const GENIE_SECTION_SUGGESTIONS: Record<
  GenieWindow,
  Partial<Record<SuspicionCategory, string[]>>
> = {
  symptoms: {
    neuropatico: [
      'Documente distribución (¿guante-calcetín, focal o multifocal?) y si hay compromiso sensitivo.',
      'Explore reflejos miotáticos y busque atrofia/fasciculaciones.',
    ],
    miopatico: [
      'Confirme patrón de debilidad proximal (cinturas) y CK sérica si está disponible.',
      'Pregunte por mialgias, intolerancia al ejercicio o antecedentes familiares.',
    ],
    unm: [
      'Indague fatigabilidad fluctuante (empeora con actividad, mejora con reposo).',
      'Busque ptosis, diplopía o debilidad bulbar variable.',
    ],
    central: [
      'Verifique signos de motoneurona superior (hiperreflexia, Babinski, espasticidad).',
      'Si coexisten MNS + MNI, considere enfermedad de motoneurona.',
    ],
  },
  ncs: {
    neuropatico: [
      'Estudie al menos un nervio sensitivo y uno motor por extremidad afectada.',
      'En sospecha distal simétrica: sural + peroneo/tibial; agregue comparativas si hay atrapamiento.',
      'Si hay enlentecimiento, descarte temperatura baja antes de concluir desmielinización.',
    ],
    miopatico: [
      'La NCS suele ser normal en miopatía; documéntelo para sustentar el patrón.',
      'Confirme que CMAP/SNAP son normales antes de pasar a EMG de aguja.',
    ],
    unm: [
      'Realice estimulación repetitiva (RNS) a 2–3 Hz en trapecio/facial.',
      'Si RNS es normal y la sospecha es alta, considere SFEMG (jitter).',
      'En sospecha de LEMS: evalúe facilitación del CMAP tras ejercicio breve.',
    ],
    central: [
      'SNAP normales con compromiso motor apoyan lesión proximal al ganglio o motoneurona.',
      'Confirme conducción motora para descartar bloqueos (diferencial con NMM).',
    ],
  },
  emg: {
    neuropatico: [
      'Muestree músculos de distinta raíz/nervio para mapear la distribución.',
      'Documente actividad espontánea y morfología/reclutamiento de PAUMs.',
    ],
    miopatico: [
      'Incluya músculos proximales (deltoides, iliopsoas) y busque PAUMs pequeños/cortos.',
      'Reclutamiento precoz y descargas miotónicas apoyan el patrón miopático.',
    ],
    unm: [
      'La aguja puede mostrar PAUMs inestables; correlacione con RNS/SFEMG.',
    ],
    central: [
      'Para criterios Gold Coast/Awaji: muestree ≥2 regiones (bulbar, cervical, torácica, lumbar).',
      'Busque denervación activa + reinervación crónica en regiones múltiples.',
    ],
  },
};
